import React, { useState, useEffect, useMemo } from 'react';
import { multiply, divide, add } from 'mathjs';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarColumnsButton,
  useGridApiRef,
  GridRowModes,
} from '@mui/x-data-grid-premium';
import FactoryIcon from '@mui/icons-material/Factory';
import { Box, Button } from '@mui/material';

import { dataGridPremiumColumns } from '../../utils/helper';
import { CheckboxUI, ConfirmDialogUI, DataGridIdentifiers, DatagridPremiumUi, InputUI } from '../Interface';
import prodgeneratorPartialService from '../../service/prodgeneratorPartial.service';
import useUsers from '../../context/UsersContext';
import useConfig from '../../context/ConfigContext';
import { useLoader } from '../../provider/LoaderProvider';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { sxConfig } from './ProductionGeneratorPartial.style';
import { getProductionQuantityForOrder } from './productionGeneratorPartial.utils';

const ProductionGeneratorPartialNewProdTable = ({
  removedItemState,
  refetchState,
  selectedItemState,
  selectedRowsState,
  getTreeData,
  selectedOrderState,
  newProdsState,
  getProdsAndFromWhs,
}) => {
  const apiRef = useGridApiRef();
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { showLoader, hideLoader } = useLoader();

  const { removedItem, setRemovedItem } = removedItemState;
  const { setRefetchTable } = refetchState;
  const { selectedItem, setSelectedItem } = selectedItemState;
  const { setSelectedRows } = selectedRowsState;
  const { selectedOrder } = selectedOrderState;
  const { newProds, setNewProds } = newProdsState;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
  }, []);

  const calculateWeight = (quantity, weightPerItem) => {
    const normalizedQuantity = Number(quantity);
    const normalizedWeightPerItem = Number(weightPerItem);

    if (!Number.isFinite(normalizedQuantity) || !Number.isFinite(normalizedWeightPerItem)) {
      return null;
    }

    return multiply(normalizedQuantity, normalizedWeightPerItem);
  };

  const clearEditRows = () => {
    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);
    for (const rowId of rowsInEditMode) {
      apiRef.current.stopRowEditMode({ id: rowId });
    }
  };

  useEffect(() => {
    if (removedItem) {
      const removedArticleNumber = removedItem.relatedvariantItemCode;
      const removedFinishedItemCode = removedItem.variantMainItemCode ?? removedItem.rootItemCode;
      const removedVariantItemCode = removedItem.relatedItemCode;
      const removedVariantFinishedItemCode =
        removedItem.level === 1 ? removedItem.relatedItemCode : removedItem.parentItemCode;

      setNewProds((prev) =>
        prev.filter((prod) => {
          const isSameRow =
            prod.baseDocType === selectedOrder.docTypeId &&
            prod.baseId === selectedOrder.docId &&
            prod.baseLine === selectedOrder.lineNum;

          if (!isSameRow) return true;

          // Törlés csak akkor, ha az ÖSSZES azonosító egyezik
          const allMatch =
            prod.articleNumber === removedArticleNumber &&
            prod.finishedItemCode === removedFinishedItemCode &&
            prod.variantItemCode === removedVariantItemCode &&
            prod.variantFinishedItemCode === removedVariantFinishedItemCode;

          return !allMatch;
        })
      );

      clearEditRows();
    }
  }, [removedItem]);

  const createRows = async () => {
    if (!selectedItem) return;
    if (!selectedOrder) return;
    showLoader();

    const generatedProds = [];

    const reference = [selectedOrder.reference, selectedOrder.itemReference].filter(Boolean).join(' - ');

    const inProd = newProds.find(
      (prod) =>
        prod.level === selectedItem.level - 1 &&
        prod.articleNumber === selectedItem.parentItemCode &&
        prod.baseId === selectedOrder.docId &&
        prod.baseLine === selectedOrder.lineNum &&
        prod.baseDocType === selectedOrder.docTypeId
    );

    // itt lehet, hogy ki kell keresni a parentet, és számolni a parent arányaival is

    const prodQty =
      selectedItem.level === 1
        ? getProductionQuantityForOrder(selectedOrder)
        : multiply(
            inProd?.productionQuantity ?? getProductionQuantityForOrder(selectedOrder),
            divide(selectedItem.neededQuantity ?? 1, selectedItem.rootBaseQuantity ?? 1)
          );

    const sd = {
      baseId: selectedOrder.docId,
      baseLine: selectedOrder.lineNum,
      baseDocNum: selectedOrder.docNum,
      baseDocType: selectedOrder.docTypeId,
      companyId: selectedOrder.companyId,
      endDate: selectedOrder.dueDate,
      articleNumber: selectedItem.relatedvariantItemCode,
      finishedItemCode: selectedItem.variantMainItemCode ?? selectedItem.rootItemCode,
      variantItemCode: selectedItem.relatedItemCode,
      variantFinishedItemCode: selectedItem.level === 1 ? selectedItem.relatedItemCode : selectedItem.parentItemCode,
      variantNote: selectedItem.sourceList.variantNote,
      joker_int1: selectedItem.sourceList.joker_int1,
      joker_int2: selectedItem.sourceList.joker_int2,
      joker_int3: selectedItem.sourceList.joker_int3,
      joker_int4: selectedItem.sourceList.joker_int4,
      joker_int5: selectedItem.sourceList.joker_int5,
      joker_int6: selectedItem.sourceList.joker_int6,
      joker_int7: selectedItem.sourceList.joker_int7,
      joker_char1: selectedItem.sourceList.joker_char1,
      joker_char2: selectedItem.sourceList.joker_char2,
      joker_char3: selectedItem.sourceList.joker_char3,
      joker_char4: selectedItem.sourceList.joker_char4,
      joker_char5: selectedItem.sourceList.joker_char5,
      joker_char6: selectedItem.sourceList.joker_char6,
      joker_char7: selectedItem.sourceList.joker_char7,
      joker_char8: selectedItem.sourceList.joker_char8,
      joker_char9: selectedItem.sourceList.joker_char9,
      joker_char10: selectedItem.sourceList.joker_char10,
      joker_char11: selectedItem.sourceList.joker_char11,
      joker_char12: selectedItem.sourceList.joker_char12,
      joker_char13: selectedItem.sourceList.joker_char13,
      joker_char14: selectedItem.sourceList.joker_char14,
      joker_char15: selectedItem.sourceList.joker_char15,
      joker_char16: selectedItem.sourceList.joker_char16,
      joker_char17: selectedItem.sourceList.joker_char17,
      joker_char18: selectedItem.sourceList.joker_char18,
      joker_char19: selectedItem.sourceList.joker_char19,
      joker_char20: selectedItem.sourceList.joker_char20,
      joker_char21: selectedItem.sourceList.joker_char21,
      joker_char22: selectedItem.sourceList.joker_char22,
      joker_char23: selectedItem.sourceList.joker_char23,
      joker_char24: selectedItem.sourceList.joker_char24,
      joker_char25: selectedItem.sourceList.joker_char25,
      joker_num1: selectedItem.sourceList.joker_num1,
      joker_num2: selectedItem.sourceList.joker_num2,
      joker_num3: selectedItem.sourceList.joker_num3,
      joker_num4: selectedItem.sourceList.joker_num4,
      joker_num5: selectedItem.sourceList.joker_num5,
      articleName: selectedItem.relatedItemName,
      productionQuantity: prodQty,
      projectId: selectedOrder.projectId,
      me: selectedItem.reletedQtyUnitId,
      quantityUnitCode: selectedItem.sourceList?.item?.quantityUnit?.code,
      onHand: selectedItem.onHand,
      description: selectedOrder.internalNote,
      reference,
      level: selectedItem.level,
      whsCode: selectedItem.sourceList?.whsCode,
      binId: selectedItem.sourceList?.binId,
      weight: calculateWeight(prodQty, selectedItem.sourceList?.weightPerItem),
      weightPerItem: selectedItem.sourceList?.weightPerItem,
      productionOperation:
        selectedItem.sourceList?.sourceListOperation?.map((o) => {
          const calculatedRate = divide(
            o.sourceListOperationMachine[0].neededQuantity,
            selectedItem.sourceList.baseQuantity
          );
          const baseQty = multiply(calculatedRate, prodQty);
          return {
            operationId: o.operationId,
            quantityUnitId: o.sourceListOperationMachine[0].quantityUnitId,
            resourceOneId: o.sourceListOperationMachine?.[0].resourceId,
            baseRate: calculatedRate,
            visOrder: o.visOrder,
            description: o.description,
            fixedLeadTime: o.fixedLeadTime,
            baseQty: baseQty,
            plannedQty: add(baseQty, o.fixedLeadTime ?? 0),
            dependence: o.dependence,
          };
        }) ?? [],
      productionItem:
        selectedItem.sourceList?.sourceListItem?.map((i) => {
          const calculatedRate = divide(i.neededQuantity, selectedItem.sourceList.baseQuantity);
          const quantity = multiply(calculatedRate, prodQty);
          const weightPerItem = i.weightPerItem ?? null;
          return {
            itemCode: i.itemCode,
            quantity,
            weight: calculateWeight(quantity, weightPerItem),
            weightPerItem,
            baseRate: calculatedRate,
            joker_char1: i.joker_char1,
            joker_num1: i.joker_num1,
            joker_num2: i.joker_num2,
            description: i.description,
            issueMethod: i.issueMethod,
            whsCode: i.whsCode,
            secondaryProduct: i.secondaryProduct,
          };
        }) ?? [],
    };

    generatedProds.push(sd);

    setNewProds((prev) => [...prev, ...generatedProds]);
    clearEditRows();
    setSelectedItem(null);

    hideLoader();
  };

  useEffect(() => {
    createRows();
  }, [selectedItem]);

  const handleProdQtyChange = (newValue, row) => {
    const newQty = parseFloat(newValue) || 0;
    setNewProds((prev) => {
      const index = prev.findIndex(
        (prod) =>
          prod.baseId === row.baseId &&
          prod.baseLine === row.baseLine &&
          prod.baseDocType === row.baseDocType &&
          prod.articleNumber === row.articleNumber
      );

      if (index === -1) return prev;

      const updatedProd = {
        ...prev[index],
        productionQuantity: newQty,
        weight: calculateWeight(newQty, prev[index].weightPerItem),
        productionOperation: prev[index].productionOperation?.map((o) => ({
          ...o,
          plannedQty: multiply(o.baseRate, newQty),
          baseQty: multiply(o.baseRate, newQty),
        })),
        productionItem: prev[index].productionItem?.map((i) => ({
          ...i,
          quantity: multiply(i.baseRate, newQty),
          weight: calculateWeight(multiply(i.baseRate, newQty), i.weightPerItem),
        })),
      };

      const updatedProds = [...prev];
      updatedProds[index] = updatedProd;

      return updatedProds;
    });
  };

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'baseDocNum', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (Object.keys(user).length) {
      setLoading(false);
    }
  }, [user]);

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'baseDocNum',
        headerName: 'Vere',
        type: 'string',
      },
      {
        field: 'baseLine',
        headerName: 'Sor',
        type: 'string',
      },
      {
        field: 'finishedItemCode',
        headerName: 'Késztermék',
        type: 'string',
      },
      {
        field: 'articleNumber',
        headerName: 'Cikk',
        type: 'string',
      },
      {
        field: 'articleName',
        headerName: 'Megnevezés',
        type: 'string',
      },
      {
        field: 'variantItemCode',
        headerName: 'Választott variáns cikkszám',
        type: 'string',
      },
      {
        field: 'variantFinishedItemCode',
        headerName: 'Kapcs.rend.vál.cikksz.',
        type: 'string',
      },
      {
        field: 'productionQuantity',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          return (
            <InputUI
              name="productionQuantity"
              type="number"
              variant="standard"
              fullWidth
              inputProps={{ min: 0.01, step: 'any' }}
              value={data.row.productionQuantity}
              onChange={(e) => {
                const val = e.target.value;
                const newValue = val === '' ? '' : parseFloat(val);

                if (val === '' || (newValue >= 0 && !isNaN(newValue))) {
                  handleProdQtyChange(newValue, data.row);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newValue,
                  });
                }
              }}
            />
          );
        },
      },
      {
        field: 'quantityUnitCode',
        headerName: 'ME',
        type: 'string',
      },
      {
        field: 'fromWarehouse',
        headerName: 'Raktárról',
        type: 'boolean',
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const { row } = params;
          return (
            <Box sx={sxConfig.fromWarehouseCheck}>
              <CheckboxUI
                checked={!!row.fromWarehouse}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const rowKey = `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.variantItemCode}`;
                  setNewProds((prev) =>
                    prev.map((prod) =>
                      `${prod.baseId}_${prod.baseLine}_${prod.baseDocType}_${prod.variantItemCode}` === rowKey
                        ? { ...prod, fromWarehouse: checked }
                        : prod
                    )
                  );
                }}
              />
            </Box>
          );
        },
      },
      {
        field: 'onHand',
        headerName: 'Raktáron',
        type: 'number',
      },
      {
        field: 'level',
        headerName: 'Szint',
        type: 'string',
      },
      {
        field: 'projectId',
        headerName: 'Projekt',
        type: 'string',
      },

      {
        field: 'description',
        headerName: 'Leírás',
        type: 'string',
      },
      {
        field: 'reference',
        headerName: 'Referencia',
        type: 'string',
      },
    ]);
  }, []);

  const CustomToolbar = ({ newProds, setShowConfirmDialog }) => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Button
          size="small"
          sx={{ ml: 'auto' }}
          startIcon={<FactoryIcon />}
          onClick={() => {
            setShowConfirmDialog(true);
          }}
          disabled={newProds.length === 0}
        >
          Generálás
        </Button>
      </GridToolbarContainer>
    );
  };

  const handleGenClick = async (rows) => {
    const resp = await prodgeneratorPartialService.createProduction(rows);
    setNewProds([]);
    setRemovedItem(null);
    addNotification({
      content: 'A gyártások generálása sikeres!',
      type: NotificationType.SUCCESS,
    });
    setRefetchTable(true);
    setSelectedItem(null);
    setSelectedRows([]);
    getTreeData(selectedOrder.itemCode);
    return resp;
  };

  const handleGenFromWarehouse = async (rows) => {
    if (!rows.length) return;
    const reqBody = rows.map((row) => ({
      docId: row.baseId,
      lineNum: row.baseLine,
      itemCode: row.articleNumber,
      mainItemCode: row.variantFinishedItemCode,
      variantItemCode: row.variantItemCode,
      qty: row.productionQuantity,
    }));
    return prodgeneratorPartialService.createFromWarehouse(reqBody);
  };

  return (
    <>
      {!loading && (
        <DatagridPremiumUi
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.ProductionGeneratorPartialNewProdTable}
          datagridInitialState={initialState}
          height={400}
          rows={newProds}
          columns={[...columns]}
          paginationAndfilterMode="client"
          getRowId={(row) => `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.variantItemCode}`}
          disableRowSelectionOnClick
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              newProds,
              setShowConfirmDialog,
            },
          }}
          editMode="row"
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params, event) => {
            const { row } = params;
            const generatedRowId = `${row.baseId}_${row.baseLine}_${row.baseDocType}_${row.variantItemCode}`;

            const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

            for (const rowId of rowsInEditMode) {
              if (generatedRowId !== rowId) {
                apiRef.current.stopRowEditMode({ id: rowId });
              }
            }
            const rowMode = apiRef.current.getRowMode(generatedRowId);
            if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: generatedRowId });
          }}
        />
      )}

      <ConfirmDialogUI
        handleChangeElement={async () => {
          const withWarehouse = newProds.filter((p) => p.fromWarehouse);
          const withoutWarehouse = newProds.filter((p) => !p.fromWarehouse);

          showLoader();
          addNotification({
            content: 'A gyártások generálása elkezdődött!',
            type: NotificationType.INFO,
          });

          try {
            await handleGenFromWarehouse(withWarehouse);
            await handleGenClick(withoutWarehouse);
            await getProdsAndFromWhs({
              docId: selectedOrder.docId,
              lineNum: selectedOrder.lineNum,
            });
          } finally {
            hideLoader();
            setShowConfirmDialog(false);
          }
        }}
        show={showConfirmDialog}
        onHide={() => {
          setShowConfirmDialog(false);
        }}
        message={`Elkészíti a ${newProds.filter((p) => !p.fromWarehouse).length} gyártási utasítást?`}
      />
    </>
  );
};

export default ProductionGeneratorPartialNewProdTable;
